import React, { useState, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Loader2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { uploadsApi, formazioneApi, visiteApi, dpiApi, haccpApi, sicurezzaApi } from '../lib/api';

const ENTITY_LABELS = {
  formazione: 'Corso formazione',
  visite: 'Visita medica',
  dpi: 'DPI',
  haccp: 'HACCP',
  sicurezza: 'Sicurezza',
};

const ENTITY_APIS = {
  formazione: formazioneApi,
  visite: visiteApi,
  dpi: dpiApi,
  haccp: haccpApi,
  sicurezza: sicurezzaApi,
};

const ENTITY_LABEL_FIELDS = {
  formazione: (r) => r.corso || '—',
  visite: (r) => r.tipo || '—',
  dpi: (r) => r.tipo || '—',
  haccp: (r) => r.titolo || '—',
  sicurezza: (r) => r.titolo || '—',
};

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentsPanel = ({ companyId, workers = [] }) => {
  const { data: attachments, loading, reload } = useFetch(() => uploadsApi.list({ companyId }), [companyId]);
  const [uploadOpen, setUploadOpen] = useState(false);

  const onDelete = async (a) => {
    if (!window.confirm(`Eliminare "${a.originalName}"?`)) return;
    try { await uploadsApi.remove(a.id); toast.success('File eliminato'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const onDownload = async (a) => {
    try { await uploadsApi.download(a.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore download'); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-3" data-testid="attachments-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500">
          Carica attestati di formazione, certificati di idoneità medica e altri documenti. Saranno inclusi nel pacchetto ZIP "Ispezione".
        </p>
        <button
          onClick={() => setUploadOpen(true)}
          data-testid="upload-file-btn"
          className="px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"
        >
          <Upload size={14} /> CARICA FILE
        </button>
      </div>

      {(!attachments || attachments.length === 0) ? (
        <div className="py-14 text-center border-2 border-dashed border-slate-200 rounded-xl" data-testid="attachments-empty">
          <FileText size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Nessun file caricato per questa azienda.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50/60" data-testid={`attachment-${a.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#e0e7ff] text-[#023F80] flex items-center justify-center shrink-0">
                <FileText size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 text-sm truncate">{a.originalName}</div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                  <span className="bg-slate-100 px-2 py-0.5 rounded text-[10px] font-semibold uppercase">{ENTITY_LABELS[a.entityType] || a.entityType}</span>
                  <span>{formatBytes(a.sizeBytes)}</span>
                  <span>{new Date(a.uploadedAt).toLocaleString('it-IT')}</span>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => onDownload(a)} title="Scarica" className="p-2 rounded-md hover:bg-slate-100 text-slate-700"><Download size={14} /></button>
                <button onClick={() => onDelete(a)} title="Elimina" className="p-2 rounded-md hover:bg-red-50 text-red-700"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => { setUploadOpen(false); reload(); }}
        companyId={companyId}
        workers={workers}
      />
    </div>
  );
};

const UploadDialog = ({ open, onClose, onUploaded, companyId, workers }) => {
  const [entityType, setEntityType] = useState('formazione');
  const [entityId, setEntityId] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const api = ENTITY_APIS[entityType];
  const { data: records } = useFetch(() => api.list({ companyId }), [entityType, companyId, open]);

  const recOptions = (records || []).filter((r) => r.id).map((r) => {
    const wId = r.workerId;
    const w = workers.find((x) => x.id === wId);
    const wn = w ? `${w.firstName} ${w.lastName}` : '';
    const label = ENTITY_LABEL_FIELDS[entityType](r);
    return { id: r.id, label: wn ? `${label} — ${wn}` : label };
  });

  const submit = async () => {
    if (!file) return toast.error('Seleziona un file');
    if (!entityId) return toast.error('Seleziona il record a cui collegare il file');
    setUploading(true);
    const fd = new FormData();
    fd.append('entityType', entityType);
    fd.append('entityId', entityId);
    fd.append('companyId', companyId);
    const rec = (records || []).find((r) => r.id === entityId);
    if (rec?.workerId) fd.append('workerId', rec.workerId);
    fd.append('file', file);
    try {
      await uploadsApi.upload(fd);
      toast.success('File caricato');
      setFile(null); setEntityId('');
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload');
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Carica un nuovo file</DialogTitle>
          <DialogDescription>
            Collega un attestato, certificato di idoneità o altro documento a un record esistente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Categoria</label>
            <Select value={entityType} onValueChange={(v) => { setEntityType(v); setEntityId(''); }}>
              <SelectTrigger data-testid="upload-entity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ENTITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Collega al record</label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger data-testid="upload-entity-id">
                <SelectValue placeholder={recOptions.length ? 'Seleziona record...' : 'Nessun record per questa categoria'} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {recOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">File (max 25 MB)</label>
            <input
              ref={fileInput}
              type="file"
              data-testid="upload-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#023F80] file:text-white hover:file:bg-[#1e293b]"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.zip"
            />
            {file && <p className="text-xs text-slate-500 mt-1">{file.name} — {formatBytes(file.size)}</p>}
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold">Annulla</button>
          <button onClick={submit} disabled={uploading} data-testid="upload-submit" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#1e293b] flex items-center gap-2 disabled:opacity-60">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Carica
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentsPanel;
